import { toast } from '@skillarea/lib-frontend';
import {
  BILLING_MAINTENANCE_SSE_EVENT,
  BILLING_MAINTENANCE_STREAM_PATH,
  type BillingMaintenancePublicStatusDto,
  HeartbeatEventSource,
  jsonParseSafe,
} from '@skillarea/lib-shared';
import { createStore, useStore } from 'zustand';

const SSE_MAINTENANCE_API_URL = `/api/maintenance/${BILLING_MAINTENANCE_STREAM_PATH}`;

const MATCHMAKING_MAINTENANCE_BLOCK_PREFIX =
  'Matchmaking is temporarily unavailable due to maintenance';
const MATCHMAKING_MAINTENANCE_CANCEL_PREFIX =
  'Search cancelled: maintenance mode is active';
const MATCHMAKING_MAINTENANCE_TOAST_DEDUPE_KEY =
  'matchmaking-maintenance-rejected';

type TMaintenanceState = {
  status: BillingMaintenancePublicStatusDto | null;
  syncStatus: (status: BillingMaintenancePublicStatusDto) => void;
  subscribeStatusEvents: (
    subscriber?: (eventSource: HeartbeatEventSource) => () => void,
  ) => () => void;
  syncStatusFromMatchmakingMessage: (
    message: string,
  ) => BillingMaintenancePublicStatusDto | null;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isBillingMaintenancePublicStatusDto(
  value: unknown,
): value is BillingMaintenancePublicStatusDto {
  if (!isRecord(value)) {
    return false;
  }

  if (typeof value.enabled !== 'boolean') {
    return false;
  }

  if (value.message !== undefined && typeof value.message !== 'string') {
    return false;
  }

  if (
    value.updatedAtMs !== undefined &&
    typeof value.updatedAtMs !== 'number'
  ) {
    return false;
  }

  return true;
}

function trimOptionalMessage(value: string | undefined): string | undefined {
  if (typeof value !== 'string') {
    return undefined;
  }

  const trimmedValue = value.trim();
  return trimmedValue.length > 0 ? trimmedValue : undefined;
}

function extractMaintenanceMessageByPrefix(
  message: string,
  prefix: string,
): string | undefined {
  if (!message.startsWith(prefix)) {
    return undefined;
  }

  const suffix = message.slice(prefix.length).trimStart();
  if (suffix.length === 0) {
    return undefined;
  }

  if (!suffix.startsWith(':')) {
    return trimOptionalMessage(suffix);
  }

  return trimOptionalMessage(suffix.slice(1));
}

export function isMatchmakingMaintenanceMessage(
  message: string | undefined,
): boolean {
  if (typeof message !== 'string') {
    return false;
  }

  return (
    message.startsWith(MATCHMAKING_MAINTENANCE_BLOCK_PREFIX) ||
    message.startsWith(MATCHMAKING_MAINTENANCE_CANCEL_PREFIX)
  );
}

export function extractMaintenanceMessageFromMatchmakingMessage(
  message: string | undefined,
): string | undefined {
  if (typeof message !== 'string') {
    return undefined;
  }

  return (
    extractMaintenanceMessageByPrefix(
      message,
      MATCHMAKING_MAINTENANCE_BLOCK_PREFIX,
    ) ??
    extractMaintenanceMessageByPrefix(
      message,
      MATCHMAKING_MAINTENANCE_CANCEL_PREFIX,
    )
  );
}

export function getMaintenanceBannerText(
  status: BillingMaintenancePublicStatusDto | null,
): string {
  const maintenanceMessage = trimOptionalMessage(status?.message);

  if (maintenanceMessage) {
    return maintenanceMessage;
  }

  return 'Maintenance is active. New matchmaking games are temporarily unavailable.';
}

export function getMaintenanceWarningToastText(
  message: string | undefined,
): string {
  const maintenanceMessage = trimOptionalMessage(message);

  if (maintenanceMessage) {
    return `${MATCHMAKING_MAINTENANCE_BLOCK_PREFIX}: ${maintenanceMessage}`;
  }

  return MATCHMAKING_MAINTENANCE_BLOCK_PREFIX;
}

export function showMatchmakingMaintenanceWarningToast(
  message: string | undefined,
): void {
  const warningText = getMaintenanceWarningToastText(message);

  if (typeof window !== 'undefined') {
    (
      window as typeof window & { __LAST_MAINTENANCE_WARNING__?: string }
    ).__LAST_MAINTENANCE_WARNING__ = warningText;
  }

  toast?.warning(warningText, {
    dedupeKey: MATCHMAKING_MAINTENANCE_TOAST_DEDUPE_KEY,
  });
}

export const maintenanceStore = createStore<TMaintenanceState>((set, get) => ({
  status: null,

  syncStatus(status) {
    set({ status });
  },

  subscribeStatusEvents(subscriber) {
    const eventSource = new HeartbeatEventSource(SSE_MAINTENANCE_API_URL);
    const unsubscribe = subscriber?.(eventSource);

    const statusListener = (message: MessageEvent<string>) => {
      const parsedStatus = jsonParseSafe<unknown>(message?.data);
      if (!isBillingMaintenancePublicStatusDto(parsedStatus)) {
        return;
      }

      get().syncStatus(parsedStatus);
    };

    eventSource.addEventListener(BILLING_MAINTENANCE_SSE_EVENT, statusListener);

    return () => {
      eventSource.removeEventListener(
        BILLING_MAINTENANCE_SSE_EVENT,
        statusListener,
      );
      unsubscribe?.();
      eventSource.close();
    };
  },

  syncStatusFromMatchmakingMessage(message) {
    if (!isMatchmakingMaintenanceMessage(message)) {
      return null;
    }

    const maintenanceMessage =
      extractMaintenanceMessageFromMatchmakingMessage(message);
    const nextStatus: BillingMaintenancePublicStatusDto = {
      enabled: true,
      message: maintenanceMessage,
      updatedAtMs: Date.now(),
    };

    set({ status: nextStatus });
    return nextStatus;
  },
}));

export const useMaintenanceStore = <T>(
  selector: (state: TMaintenanceState) => T,
) => useStore(maintenanceStore, selector);

export const useMaintenanceStatus = () => useMaintenanceStore((s) => s.status);

export const useIsMaintenanceEnabled = () =>
  useMaintenanceStore((s) => s.status?.enabled === true);
